<template>
	<v-container>
		<v-row>
			<v-col class="d-flex justify-center">
				<v-icon color="accent" size="80">{{ icon }}</v-icon>
			</v-col>
		</v-row>
		<v-row v-if="error.source">
			<v-col class="d-flex justify-center">
				<v-chip>{{ error.source }}</v-chip>
			</v-col>
		</v-row>

		<v-row v-if="error.title">
			<v-col class="d-flex justify-center text-center">
				<h1>{{ $t(error.title) }}</h1>
			</v-col>
		</v-row>

		<v-row v-if="error.i18n && (error.messages == null || !error.messages.length)">
			<v-col>
				<h3 class="text-center">
					{{ message }}
				</h3>
			</v-col>
		</v-row>

		<v-row v-if="showValidationMessage() && error.messages && error.messages.length">
			<v-col>
				<h2 class="text-center mb-4">{{ validationErrorTitle }}:</h2>
				<ul class="no-style" v-if="productsErrors.length > 0 && messageType === 'products'">
					<div v-for="(bundle, index) in groupBundles" :key="index">
						<h4>
							Bundle {{ bundle.bundleNumber }} -
							{{ getBundleName(bundle.bundleNumber - 1) }}
						</h4>
						<li v-for="(productsError, index) in bundle.products" :key="index">
							<h5 v-if="productsError.productNumber">
								{{ productsError.productNumber }} -
								{{ getProductName(productsError.bundleNumber - 1, productsError.productNumber - 1) }}
							</h5>
							<ul class="no-style">
								<li v-for="(constraint, ind) in productsError.constraints" :key="ind">
									<h5 v-if="constraint.context">
										{{ $t("validation_error.fields." + constraint.context) }}
									</h5>
									<ul>
										<li v-for="(message, ind) in constraint.constraintMessages" :key="ind">
											{{
												message.constraintParameters
													? $t(`validation_error.messages.${message.constraintMessage}Params`, {
															field: $t("validation_error.fields." + message.constraintField),
															...message.constraintParameters
													  })
													: $t("validation_error.messages." + message.constraintMessage, {
															0: $t("validation_error.fields." + message.constraintField)
													  })
											}}
										</li>
									</ul>
								</li>
							</ul>
						</li>
					</div>
				</ul>
				<ul v-if="messageType === 'general' && groupConstraints.length > 0" class="no-style">
					<li v-for="(constraint, ind) in groupConstraints" :key="ind">
						<h4 v-if="constraint.context">
							{{ $t("validation_error.fields." + constraint.context) }}
						</h4>
						<ul>
							<li v-for="(message, ind) in constraint.constraintMessages" :key="ind">
								{{
									message.constraintParameters
										? $t(`validation_error.messages.${message.constraintMessage}Params`, {
												field: $t("validation_error.fields." + message.constraintField),
												...message.constraintParameters
										  })
										: $t("validation_error.messages." + message.constraintMessage, {
												0: $t("validation_error.fields." + message.constraintField)
										  })
								}}
							</li>
						</ul>
					</li>
				</ul>

				<ValidationGroup v-if="messageType === 'general' && groupConstraints.length === 0" :messages="error.messages" :rootGroup="true" />
			</v-col>
		</v-row>

		<v-row v-if="error.message && showMessage">
			<v-col class="d-flex justify-center">
				<p class="text-center word-break">({{ error.message }})</p>
			</v-col>
		</v-row>
	</v-container>
</template>
<script lang="ts">
import Vue, { PropType } from "vue";
import { Error } from "@/api/models/error.model";
import ValidationGroup from "@/components/ValidationGroup.vue";
import { CommonUtil } from "@/utils/common.util";

export default Vue.extend({
	name: "ErrorInfo",
	components: {
		ValidationGroup
	},
	props: {
		icon: {
			type: String,
			default: "mdi-alert-circle-outline"
		},
		error: {
			type: Object as PropType<Error>
		},
		showMessage: {
			type: Boolean
		}
	},
	data: () => ({
		validationMessageCreated: false,
		validationErrorTitle: "",
		constraints: [] as any,
		productNumber: null,
		bundleNumber: null,
		currentBundle: null as null | number,
		productField: "",
		fieldIndex: 0,
		productsErrors: [] as any,
		groupConstraints: [] as any,
		groupBundles: [] as any,
		messageType: "",
		parameters: undefined as undefined | Record<string, string>
	}),
	created() {
		this.initializeErrorData();
	},
	watch: {
		error() {
			this.initializeErrorData();
		}
	},
	computed: {
		getBundleName() {
			return (bundleIndex: number) => {
				const bundle = this.$store["state"].shoppingCart.summary.bundles[bundleIndex];
				return bundle ? bundle.displayName : "";
			};
		},
		getProductName() {
			return (bundleIndex: number, productIndex: number) => {
				const bundle = this.$store["state"].shoppingCart.summary.bundles[bundleIndex];
				return bundle && bundle.products[productIndex] ? bundle.products[productIndex].displayName : "";
			};
		},
		message(): string {
			if (!this.error) {
				return "";
			}

			const { i18n } = this.error;
			if (!i18n) {
				return "";
			}

			return this.$translateWithFallback("messages", i18n, this.parameters);
		}
	},
	methods: {
		initializeErrorData() {
			if (!this.error) return;

			if (this.error.messages && this.error.messages.length > 0) {
				this.productsErrors = [];
				this.groupConstraints = [];
				this.createValidationMessage();
			}

			this.parameters = CommonUtil.flattenObject({ obj: this.error.parameters, prefix: "parameters" });
		},
		getErrorMessage() {
			const messagesLiteral = `messages.${this.error.i18n}`;
			const translationFromMessages = this.$t(messagesLiteral, this.parameters).toString();
			if (this.error.i18n && translationFromMessages === messagesLiteral) {
				return this.$t(this.error.i18n, this.parameters).toString();
			}

			return translationFromMessages;
		},
		showValidationMessage() {
			return this.error.status === 422;
		},
		checkAndUpdateProductNumber(childProductNumber) {
			if (childProductNumber.index >= 0 && this.productNumber === null) {
				this.productNumber = childProductNumber.index + 1;
			}
		},
		updateProductFieldByChildProduct(childProductNumber) {
			childProductNumber.children.forEach((childProductField) => {
				if (childProductField.field && this.productField === "") {
					this.productField = childProductField.field;
				}
			});
		},
		iterateChildrenBundles(children) {
			if (!children) return;

			children.forEach((childProductNumber) => {
				this.checkAndUpdateProductNumber(childProductNumber);
				if (childProductNumber.children) {
					this.updateProductFieldByChildProduct(childProductNumber);
					childProductNumber.children.forEach((element) => {
						if (this.productField === null || (element.field && element.field !== "value")) {
							this.productField = element.field;
						}
						this.iterateChildrenProducts(element.children);
					});
				}
			});
		},
		iterateChildrenProducts(children) {
			if (!children) return;

			children.forEach((childProductNumber, index, array) => {
				this.checkAndUpdateProductNumber(childProductNumber);
				if (childProductNumber.children) {
					this.updateProductFieldByChildProduct(childProductNumber);
					childProductNumber.children.forEach((element, i, arr) => {
						if (element.index) {
							this.fieldIndex = element.index;
							this.productField = element.field;
							this.iterateChildrenProducts(element.children);
						} else if (element.constraints) {
							this.constraints.push({
								...element,
								context: this.productField
							});
							if (i === arr.length - 1) {
								this.productsErrors.push({
									bundleNumber: this.currentBundle,
									productNumber: this.productNumber,
									fieldIndex: this.fieldIndex,
									constraints: this.constraints
								});
								this.constraints = [];
								this.productNumber = null;
								this.fieldIndex = 0;
								this.productField = "";
							}
						} else {
							if (this.productField === null || (element.field && element.field !== "value")) {
								this.productField = element.field;
							}
							this.iterateChildrenProducts(element.children);
						}
					});
				} else if (childProductNumber.constraints) {
					this.constraints.push({
						...childProductNumber,
						context: this.productField
					});
					if (index === array.length - 1) {
						this.productsErrors.push({
							bundleNumber: this.currentBundle,
							productNumber: this.productNumber,
							fieldIndex: this.fieldIndex,
							constraints: this.constraints
						});
						this.constraints = [];
						this.productNumber = null;
						this.fieldIndex = 0;
						this.productField = "";
					}
				}
			});
		},
		iterateChildrenGeneral(children) {
			if (!children) return;
			children.forEach((firstLevel) => {
				if (firstLevel.children) {
					if (firstLevel.field && this.productField === "") {
						this.productField = firstLevel.field;
					}
					firstLevel.children.forEach((element, i, arr) => {
						if (element.index) {
							this.productField = element.field;
							this.iterateChildrenGeneral(element.children);
						} else if (element.constraints) {
							this.constraints.push({
								...element,
								context: this.productField
							});
							if (i === arr.length - 1) {
								this.productsErrors.push({
									constraints: this.constraints
								});
								this.constraints = [];
								this.productField = "";
							}
						} else {
							if (this.productField === null || (element.field && element.field !== "value")) {
								this.productField = element.field;
							}
							this.iterateChildrenGeneral(element.children);
						}
					});
				} else if (firstLevel.constraints) {
					this.constraints.push({
						...firstLevel,
						context: firstLevel.field
					});
					this.productsErrors.push({
						constraints: this.constraints
					});
					this.constraints = [];
					this.productField = "";
				}
			});
		},
		groupConstraintsByContext(constraints) {
			let context = "";
			constraints.forEach((constraint) => {
				constraint.constraints.forEach((c) => {
					if (context === "" || c.context !== context) {
						context = c.context;
						this.groupConstraints.push({
							context: context,
							constraintMessages: []
						});
					}
					this.groupConstraints.forEach((element) => {
						if (element.context === context) {
							c.constraints.forEach((constraintMessage) => {
								element.constraintMessages.push({
									constraintMessage: constraintMessage.constraint,
									constraintParameters: CommonUtil.flattenObject({ obj: constraintMessage.parameters, prefix: "parameters" }),
									constraintField: c.field
								});
							});
						}
					});
				});
			});
		},
		groupConstraintsByContextProducts(constraints) {
			let context = "";
			constraints.forEach((c) => {
				if (context === "" || c.context !== context) {
					context = c.context;
					this.groupConstraints.push({
						context: context,
						constraintMessages: []
					});
				}
				this.groupConstraints.forEach((element) => {
					if (element.context === context) {
						c.constraints.forEach((constraintMessage) => {
							element.constraintMessages.push({
								constraintMessage: constraintMessage.constraint,
								constraintParameters: CommonUtil.flattenObject({ obj: constraintMessage.parameters, prefix: "parameters" }),
								constraintField: c.field
							});
						});
					}
				});
			});
		},
		groupProductsByBundle() {
			this.groupBundles = [];
			let products = [] as any;
			let currentBundle = null;
			this.productsErrors.forEach((product, index) => {
				if (currentBundle === null) {
					currentBundle = product.bundleNumber;
				} else if (currentBundle !== product.bundleNumber) {
					this.groupBundles.push({
						bundleNumber: currentBundle,
						products: products
					});
					products = [];
					currentBundle = product.bundleNumber;
				}
				if (index === this.productsErrors.length - 1) {
					products.push(product);
					this.groupBundles.push({
						bundleNumber: currentBundle,
						products: products
					});
				} else {
					products.push(product);
				}
			});
		},
		createValidationMessage() {
			if (!this.error.messages || (this.error.messages && !this.error.messages.length)) return;
			this.validationMessageCreated = true;

			if (this.error.messages[0].field === "bundles" && this.$route.path.includes("configuration"))
				return this.createConfigurationBundlesValidationMessage();

			this.messageType = "general";
			this.validationErrorTitle = this.$t("validation_error.title.general").toString();
			if (this.error.messages[0].field) {
				this.iterateChildrenGeneral(this.error.messages);
				//Reorganize constraints
				this.groupConstraintsByContext(this.productsErrors);
			}
		},

		createConfigurationBundlesValidationMessage() {
			this.messageType = "products";
			this.validationErrorTitle = this.$t("validation_error.title.products").toString();
			const { messages = [] } = this.error;
			if (messages.length > 0) {
				const [firstMessage] = messages;
				const { children } = firstMessage;
				if (children) {
					children.forEach((bundle, index) => {
						const { children: bundleChildren = [] } = bundle;
						const bundlesWithChildren = bundleChildren.filter((child) => child.children && child.children.length > 0) ?? [];
						if (bundlesWithChildren.length > 0) {
							this.currentBundle = index;
							bundlesWithChildren.forEach((bundle) => this.iterateChildrenBundles(bundle.children));
						}
					});
				}
			}
			//Reorganize constraints and products
			this.productsErrors.forEach((product) => {
				this.groupConstraintsByContextProducts(product.constraints);
				product.constraints = this.groupConstraints;
				this.groupConstraints = [];
			});
			this.groupProductsByBundle();
		}
	}
});
</script>

<style lang="scss" scoped>
h4 {
	width: inherit;
}
</style>
